'use client';

// Third-party
import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import Image from 'next/image';
import { useToggle } from '@hooks';
import cx from 'classnames';

// Utils
import { imagekitLoader } from '@utils/imagekit-loader';
import { GRAY_PIXEL_DATA_URL } from '@utils/constants';
import { appendUrlParams } from '@utils/append-url-params';
import { AccountReviewSummary, AccountReviewSummarySource } from '@gql';

// UI
import { Link } from '@components/link';
import { Typography } from '@components/typography';
import { Button } from '@components/button';
import { Avatar } from '@components/avatar';
import { GoogleRatingsDisplay } from '@components/google-ratings-display';
import { AnalyticsRef } from '@components/analytics-ref';
import { SocialSection } from '@app/(public)/listings/[slug]/_components/social';
import { PhoneNumberModal } from '@app/(public)/listings/[slug]/_components/header';
import { AccountShowList } from './account-show-list';
import { ExpandableText } from '@components/expandable-text';
type Props = {
  account: {
    id: string;
    slug: string;
    avatarUrl?: string;
    heroUrl?: string;
    companyName?: string;
    title?: string;
    description?: string;
    email?: string;
    phoneNumber?: string;
    marketingWebsiteUrl?: string;
    twitterHandle?: string;
    instagramUsername?: string;
    twitterUrl?: string;
    instagramUrl?: string;
    facebookPageUrl?: string;
    youtubeUrl?: string;
    linkedinUrl?: string;
    inBusinessSince?: number;
    accountReviewSummaries: AccountReviewSummary[];
  };
};
export const AccountShow: React.FC<Props> = t0 => {
  const $ = _c(47);
  const {
    account
  } = t0;
  const {
    on: phoneNumberModalIsOpen,
    setOff: closePhoneNumberModal,
    setOn: openPhoneNumberModal
  } = useToggle();
  let t1;
  if ($[0] !== account.accountReviewSummaries) {
    t1 = account.accountReviewSummaries.find(_temp);
    $[0] = account.accountReviewSummaries;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const googleReviewSummary = t1;
  const shouldShowCTAModal = account.email || account.phoneNumber || account.marketingWebsiteUrl;
  const shouldShowSocialSection = account.facebookPageUrl || account.instagramUrl || account.twitterUrl || account.linkedinUrl || account.youtubeUrl;
  let t2;
  if ($[2] !== account.heroUrl) {
    t2 = account.heroUrl ? <div className="w-full aspect-3 sm:overflow-hidden sm:rounded-lg"><div className="relative sm:w-full h-full w-screen -ml-4 sm:ml-0"><Image fill={true} src={account.heroUrl} alt="hero asset" placeholder="blur" className="object-center object-cover" loader={imagekitLoader} blurDataURL={GRAY_PIXEL_DATA_URL} priority={true} /></div></div> : <div className="h-10" />;
    $[2] = account.heroUrl;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== account.avatarUrl || $[5] !== account.heroUrl) {
    t3 = account.avatarUrl ? <div className={cx("-mt-14 w-24 h-24 sm:w-44 sm:h-44 z-10 overflow-hidden rounded-full shadow-lg", account.heroUrl ? "sm:-mt-14" : "-mt-2 sm:mt-10")}><Avatar shape="square" avatarUrl={account.avatarUrl} /></div> : null;
    $[4] = account.avatarUrl;
    $[5] = account.heroUrl;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== account.companyName) {
    t4 = <Typography component="h1" variant="title3" variantM="title3" variantL="title2" className="overflow-hidden text-ellipsis pt-2 sm:pt-6">{account.companyName}</Typography>;
    $[7] = account.companyName;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== account.title) {
    t5 = <Typography variant="body2" variantM="body1" component="p">{account.title}</Typography>;
    $[9] = account.title;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== account.inBusinessSince) {
    t6 = account.inBusinessSince && <Typography variant="small" variantM="body2" component="p">In business since {account.inBusinessSince}</Typography>;
    $[11] = account.inBusinessSince;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== account.email || $[14] !== account.facebookPageUrl || $[15] !== account.instagramUrl || $[16] !== account.linkedinUrl || $[17] !== account.marketingWebsiteUrl || $[18] !== account.phoneNumber || $[19] !== account.twitterUrl || $[20] !== account.youtubeUrl || $[21] !== closePhoneNumberModal || $[22] !== openPhoneNumberModal || $[23] !== phoneNumberModalIsOpen || $[24] !== shouldShowCTAModal || $[25] !== shouldShowSocialSection) {
    t7 = shouldShowCTAModal && <div className="w-full mt-4 z-40 p-4 space-y-4 -mx-4"><div className="flex flex-row space-x-2 pb-2">{account.marketingWebsiteUrl && <Link isButton={true} to={appendUrlParams(account.marketingWebsiteUrl, {
          utm_source: "savvy",
          utm_medium: "website"
        })} target="_blank" size="sm" rel="noreferrer" variant="filled" color="primary">View Website</Link>}{account.phoneNumber && <div className=""><PhoneNumberModal isOpen={phoneNumberModalIsOpen} closeModal={closePhoneNumberModal} phoneNumber={account.phoneNumber} /><AnalyticsRef name="select_account_phone"><Button variant="outlined" onClick={openPhoneNumberModal} size="sm">Call Property Manager</Button></AnalyticsRef></div>}</div>{shouldShowSocialSection && <SocialSection contacts={{
        facebookPageUrl: account.facebookPageUrl,
        instagramUrl: account.instagramUrl,
        twitterUrl: account.twitterUrl,
        linkedinUrl: account.linkedinUrl,
        youtubeUrl: account.youtubeUrl,
        publicEmail: account.email,
        publicPhoneNumber: account.phoneNumber
      }} shouldShowSocialMedia={true} shouldShowWebsiteUrlAsButton={true} />}</div>;
    $[13] = account.email;
    $[14] = account.facebookPageUrl;
    $[15] = account.instagramUrl;
    $[16] = account.linkedinUrl;
    $[17] = account.marketingWebsiteUrl;
    $[18] = account.phoneNumber;
    $[19] = account.twitterUrl;
    $[20] = account.youtubeUrl;
    $[21] = closePhoneNumberModal;
    $[22] = openPhoneNumberModal;
    $[23] = phoneNumberModalIsOpen;
    $[24] = shouldShowCTAModal;
    $[25] = shouldShowSocialSection;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const t8 = account.description || "";
  let t9;
  if ($[27] !== t8) {
    t9 = <ExpandableText text={t8} maxLines={5} maxLength={250} className="mt-4 pr-4 text-primary-main" typographyVariant="body2" typographyVariantM="body2" buttonColor="primary" buttonVariant="text" buttonClassName="underline pr-8" />;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== googleReviewSummary) {
    t10 = googleReviewSummary && <div className="pt-4"><GoogleRatingsDisplay reviewSummary={googleReviewSummary} /></div>;
    $[29] = googleReviewSummary;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t10 || $[32] !== t3 || $[33] !== t4 || $[34] !== t5 || $[35] !== t6 || $[36] !== t7 || $[37] !== t9) {
    t11 = <div className="flex flex-col md:w-1/3 space-y-2">{t3}{t4}{t5}{t6}{t7}{t9}{t10}</div>;
    $[31] = t10;
    $[32] = t3;
    $[33] = t4;
    $[34] = t5;
    $[35] = t6;
    $[36] = t7;
    $[37] = t9;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== account.slug) {
    t12 = <div className="flex flex-col md:w-2/3"><AccountShowList accountSlug={account.slug} /></div>;
    $[39] = account.slug;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== t11 || $[42] !== t12) {
    t13 = <div className="mx-auto px-4 sm:px-0"><div className="flex flex-col md:flex-row md:space-x-10 sm:pl-4">{t11}{t12}</div></div>;
    $[41] = t11;
    $[42] = t12;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== t13 || $[45] !== t2) {
    t14 = <div className="px-0 pb-8 body-container text-primary-main">{t2}{t13}</div>;
    $[44] = t13;
    $[45] = t2;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  return t14;
};
function _temp(reviewSummary) {
  return reviewSummary.source === AccountReviewSummarySource.GoogleReviews;
}